import React, { createContext, useContext, useState, useCallback } from 'react';

interface ModalItem {
  id: string;
  priority: number; // Higher = closes first (gallery: 9999, dialogs: 1000)
  onClose: () => void;
}

interface ModalStackContextType {
  registerModal: (item: ModalItem) => () => void; // Returns unregister function
  closeTopModal: () => boolean; // Returns true if modal was closed
  hasOpenModals: boolean;
  modalStack: ModalItem[]; // Sorted by priority desc
}

const ModalStackContext = createContext<ModalStackContextType | undefined>(undefined);

export function ModalStackProvider({ children }: { children: React.ReactNode }) {
  const [modalStack, setModalStack] = useState<ModalItem[]>([]);

  const registerModal = useCallback((item: ModalItem) => {
    setModalStack((prev) => {
      // Add new modal and sort by priority (highest first)
      const newStack = [...prev, item];
      return newStack.sort((a, b) => b.priority - a.priority);
    });

    // Return cleanup function
    return () => {
      setModalStack((prev) => prev.filter((m) => m.id !== item.id));
    };
  }, []);

  const closeTopModal = useCallback(() => {
    if (modalStack.length === 0) return false;

    const topModal = modalStack[0];
    topModal.onClose();
    return true;
  }, [modalStack]);

  const hasOpenModals = modalStack.length > 0;

  return (
    <ModalStackContext.Provider
      value={{
        registerModal,
        closeTopModal,
        hasOpenModals,
        modalStack,
      }}
    >
      {children}
    </ModalStackContext.Provider>
  );
}

export function useModalStack() {
  const context = useContext(ModalStackContext);
  if (!context) {
    throw new Error('useModalStack must be used within ModalStackProvider');
  }
  return context;
}
